import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Container, Paper, Alert, Snackbar } from '@mui/material';
import { EmailService } from '../../services/email.service';
import { useNavigate } from 'react-router-dom';

const ADMIN_EMAIL = 'contact@tacticafoot.com';

export const ContactForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const subject = `Message de contact - ${formData.name} via TacticaFoot`;
      const content = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #1976d2;">Nouveau message de contact - TacticaFoot</h2>
          <div style="background-color: #f5f5f5; padding: 15px; border-radius: 4px; margin: 15px 0;">
            <p><strong>Nom :</strong> ${formData.name}</p>
            <p><strong>Email :</strong> ${formData.email}</p>
            <p><strong>Message :</strong></p>
            <p style="white-space: pre-wrap;">${formData.message.replace(/\n/g, '<br/>')}</p>
          </div>
          <p style="color: #666; font-size: 12px; margin-top: 20px;">
            Ce message a été envoyé via le formulaire de contact de TacticaFoot.
          </p>
        </div>
      `;
      
      await EmailService.sendEmail(ADMIN_EMAIL, subject, content);
      setSuccess(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setError('Une erreur est survenue lors de l\'envoi du message. Veuillez réessayer.');
      console.error('Error sending contact form:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Container maxWidth="sm">
      <Paper 
        elevation={6} 
        sx={{ 
          p: 4, 
          mt: 4,
          backgroundColor: 'rgba(18, 18, 18, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{ 
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: '#fff',
            mb: 4
          }}
        >
          CONTACTEZ-NOUS
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label="Nom"
            name="name"
            autoComplete="name"
            value={formData.name}
            onChange={handleChange}
            disabled={loading}
            sx={{ 
              mb: 2,
              '& .MuiInputLabel-root': { color: 'grey.400' },
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'grey.700' },
                '&:hover fieldset': { borderColor: 'grey.500' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
              }
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Adresse email"
            name="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
            sx={{ 
              mb: 2,
              '& .MuiInputLabel-root': { color: 'grey.400' },
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'grey.700' },
                '&:hover fieldset': { borderColor: 'grey.500' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
              }
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="message"
            label="Message"
            id="message"
            multiline
            rows={4}
            value={formData.message}
            onChange={handleChange}
            disabled={loading}
            sx={{ 
              mb: 3,
              '& .MuiInputLabel-root': { color: 'grey.400' },
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'grey.700' },
                '&:hover fieldset': { borderColor: 'grey.500' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
              }
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 2,
              mb: 2,
              py: 1.5,
              backgroundColor: 'primary.main',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            {loading ? 'Envoi en cours...' : 'Envoyer le message'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              navigate('/?section=pricing');
              setTimeout(() => {
                const pricingSection = document.getElementById('pricing');
                if (pricingSection) {
                  pricingSection.scrollIntoView({ behavior: 'smooth' });
                }
              }, 100);
            }}
            sx={{
              py: 1.5,
              color: 'white',
              borderColor: 'white',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Voir les tarifs
          </Button>
        </Box>
      </Paper>

      <Snackbar 
        open={success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Votre message a été envoyé avec succès !
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};
